import { HttpStatus } from '@/core/shared/errors/enums/http-status.enum';
import { InternalError } from '@/core/shared/errors/internal.error';
import {
  JobValidationError,
  type JobValidationFieldError,
} from '@/core/shared/errors/job-validation.error';
import { LocationNotFoundError } from '@/core/shared/errors/location-not-found.error';

const CITY_STATE_NOT_FOUND = 'cityStateNotFound';

const REJECTION_STATUSES = new Set<number | null>([
  HttpStatus.BAD_REQUEST,
  HttpStatus.UNPROCESSABLE_ENTITY,
]);

type RejectionContext = {
  location?: string | null;
  fallbackMessage: string;
  error: unknown;
  data: Record<string, unknown>;
};

/**
 * Per-field details of a 400/422 that ats-project forwarded from ff-rest. The body arrives
 * parsed (`.json()`) as `{ statusCode, message, details }`, or as that same JSON in a string.
 */
export function parseProjectRejectionFields(
  statusCode: number | null,
  body: unknown,
): JobValidationFieldError[] | null {
  if (!REJECTION_STATUSES.has(statusCode)) return null;

  let parsed: unknown = body;
  if (typeof body === 'string') {
    try {
      parsed = JSON.parse(body);
    } catch {
      return null;
    }
  }
  if (typeof parsed !== 'object' || parsed === null) return null;

  const details = (parsed as { details?: unknown }).details;
  if (!Array.isArray(details)) return null;

  const fields = details.filter(
    (detail: unknown): detail is JobValidationFieldError =>
      detail !== null &&
      typeof detail === 'object' &&
      typeof (detail as { path?: unknown }).path === 'string',
  );
  return fields.length > 0 ? fields : null;
}

/**
 * The error to throw for a failed project create/duplicate: the location the user typed
 * when the backend could not place it, the offending fields when it refused the body, and
 * the generic failure for everything else (network, 5xx).
 */
export function toProjectRequestError(
  statusCode: number | null,
  body: unknown,
  context: RejectionContext,
): Error {
  const fields = parseProjectRejectionFields(statusCode, body);

  if (fields?.some((field) => field.code === CITY_STATE_NOT_FOUND)) {
    return new LocationNotFoundError(context.location ?? '', statusCode);
  }
  if (fields) {
    return new JobValidationError(fields, statusCode);
  }
  return new InternalError(context.fallbackMessage, {
    error: context.error,
    status: statusCode,
    data: context.data,
  });
}
